import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, ExternalLink } from "lucide-react";
import { invoiceAPI } from "../../services/api";
import { money, fmtDate, STATUS_BADGE } from "../../pages/invoices/invoiceUtils";
import type { Invoice, InvoiceStatus } from "../../types/invoice";

/**
 * Compact list of invoices/credit notes linked to a source record. Rendered on
 * the shipment form (edit mode), the work detail modal and the container's own
 * page, so users can see — and open — every invoice a source has been billed on.
 * Because a source can be invoiced multiple times, this can list several
 * documents.
 *
 * Exactly one of the three ids is passed; the API filters by whichever arrives.
 */
const InvoiceRefs: React.FC<{
  shipmentId?: string;
  workId?: string;
  containerLineId?: string;
  className?: string;
}> = ({ shipmentId, workId, containerLineId, className = "" }) => {
  const { t } = useTranslation("invoicing");
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!shipmentId && !workId && !containerLineId) return;
    let alive = true;
    setLoading(true);
    invoiceAPI
      .list({ shipmentId, workId, containerLineId, limit: 50 })
      .then((res) => {
        if (alive) setInvoices(res.data.data.invoices ?? []);
      })
      .catch(() => {
        if (alive) setInvoices([]);
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
          setLoaded(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [shipmentId, workId, containerLineId]);

  // Nothing to show yet, or none on file — stay out of the way.
  if (loading && !loaded) return null;
  if (loaded && invoices.length === 0) return null;

  const statusText = (inv: Invoice) =>
    inv.invoiceType === "credit_note"
      ? t("status.creditNote")
      : t(`status.${inv.status}`);

  return (
    <div
      className={`rounded-md border border-[rgba(11,15,14,0.08)] bg-white ${className}`}
    >
      <div className="px-4 py-2.5 border-b border-[rgba(11,15,14,0.06)] flex items-center gap-2">
        <FileText className="h-4 w-4 text-[#C97A3C]" />
        <span className="text-[13px] font-semibold text-[#0B0F0E]">
          {t("refs.title")}
        </span>
        <span className="text-[11px] text-[#8A9590]">
          {t("refs.count", { count: invoices.length })}
        </span>
      </div>
      <div className="divide-y divide-[rgba(11,15,14,0.06)]">
        {invoices.map((inv) => (
          <button
            type="button"
            key={inv._id}
            onClick={() => navigate(`/invoices/${inv._id}`)}
            className="w-full flex items-center gap-3 px-4 py-2 text-[13px] hover:bg-[#F8F8F6] text-left"
          >
            <span className="font-medium text-[#0B0F0E]">
              {inv.invoiceNumber}
            </span>
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
                STATUS_BADGE[inv.status as InvoiceStatus]
              }`}
            >
              {statusText(inv)}
            </span>
            <span className="text-[#8A9590]">{fmtDate(inv.invoiceDate)}</span>
            <span
              className={`ml-auto font-medium ${
                inv.invoiceType === "credit_note" ? "text-[#A32D2D]" : ""
              }`}
            >
              {money(inv.total)}
            </span>
            <ExternalLink className="h-3.5 w-3.5 text-[#b6b6b1]" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default InvoiceRefs;
